import { ReactElement, useEffect } from "react"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { OrganizationStatus } from "@/modules/organizations/enums/organization-status"
import { PlanType } from "@/modules/organizations/types/plan-type"
import { AppRole } from "@/modules/auth/constants/app-role"

interface ProtectedRouteProps {
  children?: ReactElement
  plan?: PlanType[]
  roles?: AppRole[]
  permissions?: string[]
}

function resolveRedirect(currentPlan: PlanType | undefined): string {
  if (!currentPlan) return "/select-organisation"
  return "/dashboard"
}

export function ProtectedRoute({ children, plan, roles, permissions }: ProtectedRouteProps) {
  const { isAuthenticated, hasRole, hasPermission, user } = useAuth()
  const { currentOrganization, organizations, isLoading: orgsLoading } = useOrganizationsService()
  const currentOrganizationId = useSelector(selectCurrentOrganizationId)
  const location = useLocation()
  const navigate = useNavigate()

  // An organisation is selected but the org list hasn't resolved it yet — e.g.
  // right after returning from an external payment redirect, where Redux was
  // reset and the list is still reloading (OrganizationLoader lives inside this
  // route's children, so it hasn't run yet on first render). Wait for it instead
  // of bouncing to the organisation picker.
  const orgContextResolving =
    !!currentOrganizationId &&
    !currentOrganization &&
    (orgsLoading || organizations.length === 0)

  const isActiveOrg = currentOrganization?.status === OrganizationStatus.Active
  const hasPlanAccess = !plan || (!!currentOrganization?.plan && plan.includes(currentOrganization.plan))

  const redirectTo = resolveRedirect(currentOrganization?.plan)

  // Only check org status when a specific plan is required
  const planDenied = plan != null && (!hasPlanAccess || !isActiveOrg)
  // Roles: if roles are specified, the user must have at least one of them
  const roleDenied = !!roles && roles.length > 0 && !hasRole(roles)
  // Permissions: if permissions are specified, the user must have all of them
  const permissionDenied = !!permissions && permissions.length > 0 && !hasPermission(permissions)
  
  const needsLegalConsent = user?.user?.needsLegalConsent === true

  const canAccess = !planDenied && !roleDenied && !permissionDenied && (!needsLegalConsent || location.pathname === '/accept-legal-documents')

  useEffect(() => {
    if (isAuthenticated && !canAccess && !orgContextResolving) {
      const targetPath = needsLegalConsent ? '/accept-legal-documents' : redirectTo
      console.warn(`[ProtectedRoute] Access denied. Redirecting to ${targetPath}.`)
      navigate(targetPath, { replace: true })
    }
  }, [isAuthenticated, canAccess, orgContextResolving, redirectTo, navigate, needsLegalConsent])

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // Render children while the selected org resolves so OrganizationLoader (a
  // descendant) can populate it; the effect above redirects only once we're
  // certain access is denied.
  if (!canAccess && !orgContextResolving) {
    return <Navigate to={needsLegalConsent ? '/accept-legal-documents' : redirectTo} replace />
  }

  return children ?? <Outlet />
}
